import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { crmSyncAPI } from '../services/api';
import { getPropertyName, getPropertyDisplayName } from '../utils/propertyUtils';
import { setPropertyData, setPropertyId } from '../services/tokenService';
import { 
  User, Mail, Phone, Building2, ChevronDown, Check,
  FileText, CreditCard, Landmark, ExternalLink, RefreshCw,
  MapPin, Loader2, Download, AlertCircle, Eye, Wallet,
  Calendar, IndianRupee, Clock, Percent, ClipboardList, ChevronRight, X
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AppHeader from '../components/AppHeader';
import PropertyGstCard from '../components/PropertyGstCard';
import PropertyMenuCard from '../components/PropertyMenuCard';

const ProfileScreen = () => {
  const navigate = useNavigate();
  const { user, properties, selectedProperty, setSelectedProperty } = useAuth();
  const { toast } = useToast();
  
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [crmProfile, setCrmProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  // iter108 — Fullscreen QR viewer
  const [qrFullscreen, setQrFullscreen] = useState(null);

  // Pure CRM profile loader — returns data, performs NO state updates.
  const loadProfileData = useCallback(async (propertyId) => {
    try {
      const response = await crmSyncAPI.getProfile(propertyId);
      if (response?.STATUS === 1 && response?.RESULT) {
        return { result: response.RESULT, error: null };
      }
      return { result: null, error: response?.MESSAGE || 'Profile data not found.' };
    } catch (error) {
      console.error('Error fetching CRM profile:', error);
      return {
        result: null,
        error:
          error?.response?.data?.detail
          || error?.message
          || 'Could not load profile. Pull to retry.',
      };
    }
  }, []);

  // Retry/refresh handler — invoked from user events (state updates here are
  // event-driven, not synchronous-in-effect).
  const handleRetry = useCallback(async () => {
    if (!selectedProperty?.id) {
      setLoading(false);
      setProfileError('Please select a property first.');
      return;
    }
    setLoading(true);
    setProfileError(null);
    const { result, error } = await loadProfileData(selectedProperty.id);
    setCrmProfile(result);
    setProfileError(error);
    setLoading(false);
  }, [selectedProperty, loadProfileData]);

  // Initial load — all state updates happen AFTER the network await.
  useEffect(() => {
    if (!selectedProperty?.id) return undefined;
    let active = true;
    (async () => {
      const { result, error } = await loadProfileData(selectedProperty.id);
      if (!active) return;
      setCrmProfile(result);
      setProfileError(error);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [selectedProperty, loadProfileData]);

  const handlePropertyChange = (property) => {
    setSelectedProperty(property);
    setPropertyData(property);
    setPropertyId(property.id);
    setShowPropertyDropdown(false);
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return 'N/A';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  // Format percentage
  const formatPercent = (val) => {
    if (val === null || val === undefined || val === '') return 'N/A';
    return `${Number(val)}%`;
  };

  // Mask sensitive data helper
  const maskAccountNumber = (num) => {
    if (!num) return 'N/A';
    const str = String(num);
    if (str.length <= 4) return str;
    return '****' + str.slice(-4);
  };

  // Extract data from CRM profile
  const baseInfo = crmProfile?.base_info || {};
  const pefDocument = crmProfile?.pef_document || {};
  const paymentInfo = crmProfile?.payment_info || {};
  const bankDetails = crmProfile?.bank_details || {};
  const documents = crmProfile?.documents || {};
  const commercials = crmProfile?.commercials || {};

  // OTA links can be array or object — normalise to array
  const otaRaw = crmProfile?.ota_links;
  const otaLinks = (Array.isArray(otaRaw) ? otaRaw : []).filter(ota => ota.ota_name && ota.ota_name.trim());

  // Document checklist fields (from `documents` blob — strictly CRM data)
  const docChecklistFields = [
    { key: 'master_form', label: 'Master Form' },
    { key: 'noc_agreement', label: 'NOC Agreement' },
    { key: 'lease_agreement', label: 'Lease Agreement' },
    { key: 'electricity_bill', label: 'Electricity Bill' },
    { key: 'pan_card', label: 'PAN Card' },
    { key: 'aadhar_card', label: 'Aadhar Card' },
    { key: 'gst_certificate', label: 'GST Certificate' },
    { key: 'cancelled_check_and_bank_details', label: 'Cancelled Cheque / Bank Details' },
    { key: 'property_photos', label: 'Property Photos' },
  ];

  // Derived UI state (avoids synchronous state writes for the no-property case)
  const noProperty = !selectedProperty?.id;
  const showLoading = loading && !noProperty;
  const effectiveError = noProperty ? 'Please select a property first.' : profileError;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24" data-testid="profile-screen">
      {/* Premium App Header */}
      <AppHeader />

      {showLoading ? (
        <div className="flex items-center justify-center py-20" data-testid="profile-loading">
          <Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" />
        </div>
      ) : effectiveError && !crmProfile ? (
        <div
          className="p-4 max-w-lg mx-auto pt-8 flex flex-col items-center text-center"
          data-testid="profile-error"
        >
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <p className="text-base font-semibold text-gray-900">Couldn&apos;t load profile</p>
          <p className="text-sm text-gray-500 mt-1">{effectiveError}</p>
          <button
            onClick={handleRetry}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B2A7B] text-white font-medium hover:bg-[#152163]"
            data-testid="profile-retry-btn"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : (
        <div className="p-4 space-y-4 max-w-lg mx-auto pt-5">
          {/* iter169 — Onboarding Form entry removed from bottom Profile tab
              per product spec. It remains accessible from the top-left profile
              menu (AppHeader → "Onboarding Form"). */}

          {/* iter170 — Owner-editable GST Number (per property). */}
          <PropertyGstCard propertyId={selectedProperty?.id} />

          {/* iter278 — Hotel menu PDF/image upload (owner-only). Lives
              directly below the GST card inside Property Details, per spec. */}
          <PropertyMenuCard propertyId={selectedProperty?.id} />

          {/* Property Information Section */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-premium overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-[#EEF1FC]/50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 tracking-tight">
                <Building2 className="w-4 h-4 text-[#1B2A7B]" />
                Property Information
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-[#EEF1FC] rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#1B2A7B]" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Property Name</p>
                  <p className="text-gray-900 font-medium">{baseInfo.property_name || getPropertyName(selectedProperty) || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Owner Name</p>
                  <p className="text-gray-900 font-medium">{baseInfo.owner_name || user?.name || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Mobile Number</p>
                  <p className="text-gray-900 font-medium">{baseInfo.phone || user?.mobile_number || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Email Address</p>
                  <p className="text-gray-900 font-medium">{baseInfo.email || user?.email || 'N/A'}</p>
                </div>
              </div>
              {baseInfo.address && (
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gray-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-400">Address</p>
                    <p className="text-gray-900 font-medium">
                      {baseInfo.address || 'N/A'}
                    </p>
                    {baseInfo.google_map_link && (
                      <a
                        href={baseInfo.google_map_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1.5 text-sm font-medium text-[#1B2A7B] hover:text-[#1B2A7B]-dark hover:underline"
                        data-testid="profile-google-maps-link"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        View on Google Maps
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 1: PEF Document (Read-only). iter120 — file_url is now a
              signed backend proxy that streams the PDF from the external CRM,
              so View/Download work directly from any browser. `download=1`
              query flag toggles Content-Disposition. */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="pef-section">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#1B2A7B]" />
                PEF Document
              </h3>
              {pefDocument && (pefDocument.file_url || pefDocument.status) && (() => {
                // iter201 — When a PEF file is actually uploaded (file_url
                // present), the on-screen badge must read "Complete" even if
                // CRM still returns an upstream status of "PENDING" (out-of-
                // band verification lag). Falls back to the raw CRM status
                // otherwise so ops keep visibility into intermediate states.
                const rawStatus = String(pefDocument.status || '').trim();
                const rawUpper = rawStatus.toUpperCase();
                const hasFile = !!pefDocument.file_url;
                const isComplete = hasFile
                  || rawUpper.includes('COLLECTED')
                  || rawUpper.includes('UPLOADED')
                  || rawUpper === 'COMPLETE'
                  || rawUpper === 'COMPLETED';
                const displayStatus = isComplete ? 'Complete' : (rawStatus || 'Pending');
                return (
                  <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isComplete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`} data-testid="pef-status-badge">
                    {displayStatus}
                  </span>
                );
              })()}
            </div>
            <div className="p-4">
              {pefDocument && pefDocument.file_url ? (
                <div className="p-3 bg-[#EEF1FC] rounded-lg border border-[#1B2A7B]/20 mb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-[#EEF1FC] rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#1B2A7B]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate" data-testid="pef-file-name">
                          {pefDocument.file_name || 'PEF Document.pdf'}
                        </p>
                        {pefDocument.uploaded_at && (
                          <p className="text-[11px] text-gray-500 mt-0.5" data-testid="pef-uploaded-at">
                            Uploaded {new Date(pefDocument.uploaded_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={pefDocument.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-2 bg-white text-navy border border-navy text-sm font-medium rounded-lg hover:bg-navy hover:text-white transition-colors"
                        data-testid="view-pef-button"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </a>
                      <a
                        href={`${pefDocument.file_url}${pefDocument.file_url.includes('?') ? '&' : '?'}download=1`}
                        className="flex items-center gap-1 px-3 py-2 bg-[#1B2A7B] text-white text-sm font-medium rounded-lg hover:bg-[#1B2A7B]-dark transition-colors"
                        data-testid="download-pef-button"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300" data-testid="pef-empty">
                  <FileText className="w-8 h-8 text-gray-300" />
                  <p className="text-sm text-gray-500">No PEF Uploaded</p>
                  <p className="text-xs text-gray-400">Document will appear here once uploaded by Admin</p>
                </div>
              )}
            </div>
          </div>

          {/* Section: Documents Checklist (strictly from crm_data.property_checklist_info) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="documents-section">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#1B2A7B]" />
                Document Checklist
              </h3>
              {documents.document_collection_status && (
                <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  documents.document_collection_status.includes('COLLECTED') ? 'bg-green-100 text-green-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {documents.document_collection_status}
                </span>
              )}
            </div>
            <div className="p-4 space-y-2" data-testid="doc-checklist-list">
              {(() => {
                // iter201 — App only surfaces checklist items that are
                // explicitly marked "Complete" / "Completed" in CRM.
                // Everything else (Pending, Not Required, NA, blank, or any
                // in-progress status) is hidden from the hotel-owner view.
                // The underlying CRM data is untouched.
                const completedItems = docChecklistFields.filter(field => {
                  const value = String(documents[field.key] || '').trim().toUpperCase();
                  return value === 'COMPLETE' || value === 'COMPLETED';
                });
                if (completedItems.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 text-center py-3" data-testid="doc-checklist-empty">
                      No documents completed yet.
                    </p>
                  );
                }
                return completedItems.map(field => (
                  <div key={field.key} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg" data-testid={`doc-${field.key}`}>
                    <span className="text-sm text-gray-700">{field.label}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                      Complete
                    </span>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Section 2: Commercials (Read-only) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="payment-info-section">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#1B2A7B]" />
                Commercials
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {/* Interested Plan */}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Interested Plan</p>
                    <p className="text-gray-900 font-semibold">{paymentInfo.interested_plan || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Payment Completed Date */}
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Payment Completed Date</p>
                    <p className="text-gray-900 font-medium">{formatDate(paymentInfo.payment_completed_date)}</p>
                  </div>
                </div>
              </div>

              {/* iter108 — Finalized Setup Fee (from CRM commercials.finalized_setup_fee, NOT Token Advance) */}
              <div className="p-4">
                <div>
                  <p className="text-xs text-gray-400">Finalized Setup Fee</p>
                  <p className="text-gray-900 font-semibold text-lg" data-testid="commercial-finalized-setup-fee">
                    {formatCurrency(commercials.finalized_setup_fee)}
                  </p>
                </div>
              </div>

              {/* Token Advance & Pending Payment */}
              <div className="grid grid-cols-2 gap-4 p-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-xs text-green-600">Token Advance</p>
                  <p className="text-green-700 font-bold text-xl">{formatCurrency(paymentInfo.token_advance)}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <p className="text-xs text-orange-600">Pending Payment</p>
                  <p className="text-orange-700 font-bold text-xl">{formatCurrency(paymentInfo.pending_payment)}</p>
                </div>
              </div>

              {/* Setup Payment Status */}
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Setup Payment Status</p>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      paymentInfo.setup_payment_status === 'Completed' ? 'bg-green-100 text-green-700' :
                      paymentInfo.setup_payment_status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {paymentInfo.setup_payment_status || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Agreed Commission */}
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#EEF1FC] rounded-lg flex items-center justify-center">
                    <Percent className="w-4 h-4 text-[#1B2A7B]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Agreed Commission</p>
                    <p className="text-[#1B2A7B] font-bold text-lg" data-testid="commercial-agreed-commission">{formatPercent(commercials.agreed_commission_percentage)}</p>
                  </div>
                </div>
              </div>

              {/* iter137 — PEF was previously duplicated here as well. The
                  canonical PEF section (with View / Download / Upload Status)
                  lives above under "Profile Information → PEF Document". This
                  duplicate row has been removed. */}
            </div>
          </div>

          {/* Section 3: Bank Details (Read-only) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="bank-section">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Landmark className="w-4 h-4 text-[#1B2A7B]" />
                Bank Details
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {/* Account Holder Name */}
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Account Holder Name</p>
                  <p className="text-gray-900 font-medium">{bankDetails.account_holder_name || 'N/A'}</p>
                </div>
              </div>

              {/* Bank Name */}
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Bank Name</p>
                  <p className="text-gray-900 font-medium">{bankDetails.bank_name || 'N/A'}</p>
                </div>
              </div>

              {/* Account Number & IFSC */}
              <div className="grid grid-cols-2 gap-4 p-4">
                <div>
                  <p className="text-xs text-gray-400">Account Number</p>
                  <p className="text-gray-900 font-medium font-mono">{maskAccountNumber(bankDetails.account_number)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">IFSC Code</p>
                  <p className="text-gray-900 font-medium font-mono">{bankDetails.ifsc_code || 'N/A'}</p>
                </div>
              </div>

              {/* Branch Name */}
              <div className="flex items-center gap-4 p-4">
                <div className="flex-1">
                  <p className="text-xs text-gray-400">Branch Name</p>
                  <p className="text-gray-900 font-medium">{bankDetails.branch_name || 'N/A'}</p>
                </div>
              </div>

              {/* UPI ID */}
              {bankDetails.upi_id && (
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-400">UPI ID</p>
                    <p className="text-gray-900 font-medium">{bankDetails.upi_id}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* iter84+iter108 — Payment QR section. Always rendered (placeholder
              shown when no QR is on file). Tap thumbnail → fullscreen viewer. */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="qr-section">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#1B2A7B]" />
                Payment QR Code
              </h3>
            </div>
            <div className="p-4 flex flex-col items-center gap-2">
              {bankDetails.qr_code_url ? (
                <button
                  type="button"
                  onClick={() => setQrFullscreen(bankDetails.qr_code_url)}
                  className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                  data-testid="profile-qr-image-wrap"
                >
                  <img
                    src={bankDetails.qr_code_url}
                    alt="Payment QR"
                    className="w-48 h-48 object-contain"
                    data-testid="profile-qr-image"
                    onError={(e) => { e.currentTarget.dataset.errored = '1'; }}
                  />
                </button>
              ) : (
                <div
                  className="w-48 h-48 flex flex-col items-center justify-center bg-gray-50 border border-dashed border-gray-300 rounded-lg text-gray-400"
                  data-testid="profile-qr-placeholder"
                >
                  <CreditCard className="w-8 h-8 mb-2" />
                  <p className="text-xs text-center px-2">QR Code Not Available</p>
                </div>
              )}
              <p className="text-xs text-gray-500 text-center">
                {bankDetails.qr_code_url
                  ? `Tap to view full screen — scan to pay ${baseInfo.property_name || 'this property'}`
                  : 'Please contact the administrator to upload a Payment QR for this property.'}
              </p>
              {bankDetails.cancelled_cheque_url && (
                <a
                  href={bankDetails.cancelled_cheque_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-[#1B2A7B] hover:text-[#1B2A7B]"
                  data-testid="profile-cheque-link"
                >
                  <Eye className="w-3 h-3" /> View Cancelled Cheque
                </a>
              )}
            </div>
          </div>

          {/* Section 4: OTA Links (Read-only) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="ota-section">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-[#1B2A7B]" />
                OTA Links
              </h3>
            </div>
            <div className="p-4">
              {otaLinks.length > 0 ? (
                <div className="space-y-3">
                  {otaLinks.map((ota, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                      data-testid={`ota-item-${index}`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{ota.ota_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {(ota.hotel_id || ota.ota_id) && <p className="text-xs text-gray-400">ID: {ota.hotel_id || ota.ota_id}</p>}
                          {ota.ota_status && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              ota.ota_status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                              ota.ota_status === 'NOT STARTED' ? 'bg-gray-100 text-gray-600' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {ota.ota_status}
                            </span>
                          )}
                        </div>
                      </div>
                      {(ota.listing_url || ota.ota_link) && (
                        <a
                          href={(ota.listing_url || ota.ota_link).startsWith('http') ? (ota.listing_url || ota.ota_link) : `https://${ota.listing_url || ota.ota_link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 bg-navy text-white text-sm rounded-lg hover:bg-navy-light transition-colors"
                          data-testid={`ota-link-${index}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Open
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                  <p className="text-sm text-gray-500">No OTA information available</p>
                </div>
              )}
            </div>
          </div>

          {/* (Settings panel and Logout moved into the top-left header dropdown menu) */}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showPropertyDropdown && (
        <div 
          className="fixed inset-0 z-20" 
          onClick={() => setShowPropertyDropdown(false)}
        />
      )}

      {/* iter108 — Fullscreen QR viewer */}
      {qrFullscreen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setQrFullscreen(null)}
          data-testid="qr-fullscreen-modal"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setQrFullscreen(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            data-testid="qr-fullscreen-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="bg-white p-4 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={qrFullscreen}
              alt="Payment QR full screen"
              className="max-w-[80vw] max-h-[80vh] object-contain"
              data-testid="qr-fullscreen-image"
            />
            <div className="mt-3 flex items-center justify-center gap-3">
              <a
                href={qrFullscreen}
                download={`payment-qr-${baseInfo.property_name || 'property'}.png`}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#1B2A7B] hover:text-[#1B2A7B]"
                data-testid="qr-fullscreen-download"
              >
                <Download className="w-3 h-3" /> Download
              </a>
              <a
                href={qrFullscreen}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                data-testid="qr-fullscreen-open-new-tab"
              >
                <ExternalLink className="w-3 h-3" /> Open in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
